// Ultimate Card Game (UCG) - Base Module System
//
// Modules are base EXTENSIONS, not field structures: playing a Module card
// never creates a Unit. The module is lowered on a cable above the team's
// main building and stacked on top of it, forming a tower. Its HP is added
// to the base's HP and the whole tower counts as one building from then on -
// a module can never be attacked, damaged or destroyed individually, it
// operates until the base itself falls.
//
// Design decisions and the full rationale: docs/MODULE_SYSTEM_PLAN.md.
//
// Three properties of this file matter for lockstep determinism
// (docs/INVARIANTS.md #1):
//
// 1. build() runs inside a replayed PLAY command (CardSystem.playCard) and
//    reads nothing but `team` and the card - no UI state, no Date.now(),
//    no Math.random(), no local inventory.
// 2. The descent is a TICK countdown (`ticksRemaining`, decremented once per
//    Battlefield.update() call), modelled on battlefield.pendingCapsules -
//    not a wall-clock timer. The renderer derives the cable position from
//    that same counter, so both peers show the same frame of the animation
//    and the renderer never owns simulation state (PERFORMANCE_PARADIGMS #11).
// 3. hasCap() - the small flag-topped reward module that appears once the
//    stack is full - is DERIVED from stack.length, never stored and never
//    transmitted. It therefore cannot desync.

// Maximum number of stackable modules on top of the foundation. Raising this
// is a balance decision, not a technical one - the geometry below and
// getSlotPosition() scale with it, and hasCap() follows automatically.
export const MODULE_MAX = 3;

// One footprint for every module, which is what makes them stackable in any
// order (the foundation module's roof uses the same footprint). Design
// pixels, matching the base's own procedural geometry in renderer.js.
export const MODULE_W = 86;
export const MODULE_H = 72;
// How the MODULE_H budget splits into the 2.5D cube: FRONT_H is the height of
// the front face, MODULE_TOP_D the vertical compression of the top face, and
// MODULE_SKEW its horizontal offset (mirrored per team). FRONT_H +
// MODULE_TOP_D is MODULE_H exactly, so slot N's box bottom is slot N+1's box
// top and the stack has no seams.
export const MODULE_TOP_D = 23;
export const MODULE_FRONT_H = MODULE_H - MODULE_TOP_D;
export const MODULE_SKEW = 13;

// The roof plane of the foundation module, relative to base.y, and the
// horizontal centre of the tower relative to base.x, mirrored per team. Both
// are consumed by getSlotPosition() only - nothing else may hardcode them.
//
// The foundation is a monolithic bunker plinth (with a dedicated dock block
// of height 65px), so the roof sits at dockY = -39.
// This value is the seam line: it is both the top edge of the dock block's
// front face and the bottom edge of slot 0's front face - see renderBase()
// in js/engine/renderer.js.
export const FOUNDATION_ROOF_DY = -39;
// Offset from base.x matching the center of the dock block on the foundation plinth.
export const MODULE_ANCHOR_DX = 4;

// Cable descent duration, in simulation ticks (Game.TICK_DT = 1/60), i.e.
// 1.5 seconds. HP and function only take effect once this reaches 0.
export const MODULE_DEPLOY_TICKS = 90;

// Per-module frame configuration. Deliberately minimal: gameplay numbers
// (hp, cost, capsuleUnit, capsuleLoadCount, ...) stay on the CARD in
// js/data/cards.js and are read from the card at build time, so there is no
// second source of truth to keep in sync. What lives here is only what
// describes the module cube itself.
export const MODULE_DEFS = {
  capsule_sling: {
    name: 'Invasion Capsule Sling',
    // How the functional part is mounted inside the steel-frame cube - see
    // the reference art in assets/cards/2_slingmodul.png: the centrifuge
    // drum rests on girders in the cube's floor (not suspended on chains),
    // with the accelerator tube angled up and out through the frame.
    interior: 'centrifuge_on_girders'
  },
  drone_command: {
    name: 'Drone Command',
    interior: 'assembly_bay'
  },
  congress_module: {
    name: 'Congress Module',
    interior: 'debating_chamber'
  }
};

// Stable index per module id, used to fold the built stack into
// calcStateHash() as a bitmask. Source order of MODULE_DEFS - appending a new
// module is safe, reordering the object is not.
export const MODULE_IDS = Object.freeze(Object.keys(MODULE_DEFS));

// ── Congress Module Decrees ──────────────────────────────────────────
export const CONGRESS_BUFF_INTERVAL_TICKS = 1200; // 20s at 60 Hz (Game.TICK_DT = 1/60)

export const CONGRESS_BUFFS = Object.freeze([
  {
    id: 'advanced_ammunition',
    name: 'ADVANCED AMMUNITION',
    bannerText: 'ADVANCED AMMUNITION +2 DMG',
    hudEffect: '+2 DMG',
    color: '#ef4444' // red / combat
  },
  {
    id: 'tariffs',
    name: 'TARIFFS',
    bannerText: 'TARIFFS +0.5 PLASMA',
    hudEffect: '+0.5 PLASMA',
    color: '#00ff88' // plasma green
  },
  {
    id: 'panzerschokolade',
    name: 'PANZERSCHOKOLADE',
    bannerText: 'PANZERSCHOKOLADE +1.5 SPEED',
    hudEffect: '+1.5 SPEED',
    color: '#f59e0b' // amber / speed
  },
  {
    id: 'quality_steel',
    name: 'QUALITY STEEL',
    bannerText: 'QUALITY STEEL +10% DEFLECTION',
    hudEffect: '+10% DEFLECT',
    color: '#38bdf8' // steel blue
  },
  {
    id: 'government_contracts',
    name: 'GOVERNMENT CONTRACTS',
    bannerText: 'GOVERNMENT CONTRACTS FREE DRAW',
    hudEffect: 'FREE DRAW',
    color: '#ffd700' // golden / contracts
  }
]);

export const CONGRESS_PROCLAMATION_INTROS = Object.freeze([
  'A GREAT DECREE:',
  'A HUGE DECREE:',
  'TREMENDOUS DECREE:',
  'BELIEVE ME:',
  'THE BEST DECREE:',
  'BIGGEST IN HISTORY:',
  'MANY PEOPLE ARE SAYING:',
  'A BEAUTIFUL DECREE:',
  'NOBODY DOES IT BETTER:',
  'TOTAL VICTORY:'
]);

export class ModuleSystem {
  constructor(battlefield, particles = null, audio = null) {
    this.battlefield = battlefield;
    this.particles = particles;
    this.audio = audio;
    this.reset();
  }

  reset() {
    // Landed modules, bottom-up. Index in this array IS the stack slot.
    this.stack = { western: [], eastern: [] };
    // Modules currently descending on their cable, in build order.
    this.pending = { western: [], eastern: [] };
    // Per-module runtime state (e.g. the Capsule Sling's load counter),
    // keyed by moduleId. Lives here rather than on a Unit because a module
    // never has one.
    this.state = { western: {}, eastern: {} };
    // Purely cosmetic per-module timers (load-lamp pulses, crane retraction).
    // Kept OUT of `state` on purpose: `state` is serialized into host snapshots,
    // and decorative visual fades have no business crossing the network.
    this.fx = {
      western: { retractingRigs: [] },
      eastern: { retractingRigs: [] }
    };
    // Bumped every time a module lands - lets the renderer/audio fire a
    // one-shot landing effect without polling, same pattern as
    // CardSystem.meltSeq.
    this.landedSeq = 0;
  }

  // ── Queries ──────────────────────────────────────────────────────────

  // Landed and operational.
  has(team, moduleId) {
    return this.stack[team].some(m => m.moduleId === moduleId);
  }

  isPending(team, moduleId) {
    return this.pending[team].some(m => m.moduleId === moduleId);
  }

  // Landed OR still descending. This is the "already spent for this match"
  // predicate - a module card can never come back, not even while its own
  // module is still on the cable.
  isClaimed(team, moduleId) {
    return this.has(team, moduleId) || this.isPending(team, moduleId);
  }

  // Slots taken, including the ones reserved by descending modules, so two
  // cables can never be assigned the same height.
  stackCount(team) {
    return this.stack[team].length + this.pending[team].length;
  }

  // The flag-topped reward module. Derived, never stored: it appears the
  // moment the last module has actually LANDED (not when it was played).
  hasCap(team) {
    return this.stack[team].length >= MODULE_MAX;
  }

  canBuild(team, moduleId) {
    if (!MODULE_DEFS[moduleId]) return false;
    if (this.isClaimed(team, moduleId)) return false;
    if (this.stackCount(team) >= MODULE_MAX) return false;
    return true;
  }

  // ── Geometry ─────────────────────────────────────────────────────────
  //
  // Single source of truth for where a module sits, exactly like
  // BuildingSystem.getAttachmentPosition(): the renderer, the cable anchor
  // and any future hit-test all read these, so the drawn position and the
  // logical position cannot drift apart.
  //
  // getSlotX/getSlotY are the frame-loop-safe scalar variants - getSlotPosition()
  // allocates an object and must not be called per frame (PERFORMANCE_PARADIGMS #1).

  getSlotX(team) {
    const base = this.battlefield.bases[team];
    return base.x + (team === 'western' ? 1 : -1) * MODULE_ANCHOR_DX;
  }

  getSlotY(team, index) {
    const base = this.battlefield.bases[team];
    return base.y + FOUNDATION_ROOF_DY - MODULE_H / 2 - index * MODULE_H;
  }

  getSlotPosition(team, index) {
    return {
      x: this.getSlotX(team),
      y: this.getSlotY(team, index),
      dir: team === 'western' ? 1 : -1
    };
  }

  // ── Building ─────────────────────────────────────────────────────────

  // Called from CardSystem.playCard() inside a replayed PLAY command. Puts
  // the module on its cable; HP and function follow in _land() once the
  // descent countdown expires.
  build(team, moduleId, cardData = null) {
    if (!this.canBuild(team, moduleId)) return false;

    this.pending[team].push({
      moduleId,
      cardData,
      // Card HP is added to the base 1:1 - HP is a per-module attribute, so
      // every module may bring a different amount (docs/MODULE_SYSTEM_PLAN.md
      // section 4). Read once here, not re-derived later.
      hp: Math.max(0, Math.round(Number(cardData?.hp) || 0)),
      // Reserved at build time so several modules played in quick succession
      // descend to different heights instead of into the same slot.
      slot: this.stackCount(team),
      ticksRemaining: MODULE_DEPLOY_TICKS,
      ticksTotal: MODULE_DEPLOY_TICKS
    });

    return true;
  }

  // Descent progress of a pending module, 0 (just played) to 1 (touching
  // down). The renderer's only input for the cable animation.
  deployProgress(entry) {
    if (!entry || !entry.ticksTotal) return 1;
    return 1 - Math.max(0, entry.ticksRemaining) / entry.ticksTotal;
  }

  // ── Simulation ───────────────────────────────────────────────────────

  // Called once per simulation tick from Battlefield.update() - NOT from the
  // render loop and not from main.js, so headless tests and both online
  // peers tick it identically.
  update(dt, particles = null, audio = null) {
    for (let t = 0; t < TEAMS.length; t++) {
      const team = TEAMS[t];
      const pending = this.pending[team];
      // Forward iteration with in-place splice, NOT the reverse loop the
      // projectile/capsule queues use: two modules played within the same
      // descent window touch down on the same tick, and landing them
      // back-to-front would push them onto the stack in reverse slot order.
      // The stack array's index must stay equal to the tower slot.
      let i = 0;
      while (i < pending.length) {
        const entry = pending[i];
        entry.ticksRemaining--;
        if (entry.ticksRemaining <= 0) {
          pending.splice(i, 1);
          this._land(team, entry, particles, audio);
        } else {
          i++;
        }
      }

      // Per-module behaviour, mirroring BuildingSystem.update()'s dispatch.
      // Only landed modules operate - one still on its cable does nothing.
      if (this.has(team, 'capsule_sling')) this.tickCapsuleSling(team, dt, particles, audio);
      if (this.has(team, 'drone_command')) this.tickDroneCommand(team, dt, particles, audio);
      if (this.has(team, 'congress_module')) this.tickCongressModule(team, dt, particles, audio);

      // Ticking cosmetic crane retraction rigs (Zero-Allocation)
      const rigs = this.fx[team]?.retractingRigs;
      if (rigs && rigs.length > 0) {
        let r = 0;
        while (r < rigs.length) {
          rigs[r].ticksRemaining--;
          if (rigs[r].ticksRemaining <= 0) {
            rigs[r] = rigs[rigs.length - 1];
            rigs.pop();
          } else {
            r++;
          }
        }
      }
    }
  }

  // ── Invasion Capsule Sling ───────────────────────────────────────────
  //
  // Moved here from Unit.update()'s section 3B when the Sling stopped being
  // a field structure. The mechanic is unchanged: friendly capsuleUnit-named
  // units are captured while they are still fresh out of the base (within
  // CAPTURE_ZONE of the team's own base.x - which is what this check always
  // measured against, even back when it ran on the structure itself), and
  // once capsuleLoadCount of them are aboard the capsule is launched at the
  // frontline. What changed is only the launch ORIGIN: the capsule now
  // leaves the module up in the tower instead of a building on the lane.
  //
  // Captures every eligible unit found in ONE pass rather than one per tick:
  // at a large dt (a slow frame, or the game's 4x speed toggle) several
  // marines can cross the zone in a single tick, and rate-limiting to one
  // would let the rest walk straight past uncaptured.
  tickCapsuleSling(team, dt, particles = null, audio = null) {
    const bf = this.battlefield;
    const stack = this.stack[team];
    let entry = null;
    for (let i = 0; i < stack.length; i++) {
      if (stack[i].moduleId === 'capsule_sling') { entry = stack[i]; break; }
    }
    if (!entry) return;

    const card = entry.cardData;
    const capsuleUnit = card?.capsuleUnit || 'Marine';
    const capacity = card?.capsuleLoadCount || 3;

    let st = this.state[team].capsule_sling;
    if (!st) {
      st = { loaded: 0, capacity };
      this.state[team].capsule_sling = st;
    }
    let fx = this.fx[team].capsule_sling;
    if (!fx) {
      fx = { pulse0: 0, pulse1: 0, pulse2: 0 };
      this.fx[team].capsule_sling = fx;
    }
    if (fx.pulse0 > 0) fx.pulse0 = Math.max(0, fx.pulse0 - dt);
    if (fx.pulse1 > 0) fx.pulse1 = Math.max(0, fx.pulse1 - dt);
    if (fx.pulse2 > 0) fx.pulse2 = Math.max(0, fx.pulse2 - dt);

    const base = bf.bases[team];
    const CAPTURE_ZONE = 180; // > the 130-160px random base-spawn offset (see Battlefield.spawnUnit)
    const units = bf.units;
    for (let i = 0; i < units.length; i++) {
      if (st.loaded >= capacity) break;
      const u = units[i];
      if (u.isDead || u.isStructure || u.isParachuting || u.isCinematic) continue;
      if (u.team !== team || u.name !== capsuleUnit) continue;
      if (Math.abs(u.x - base.x) > CAPTURE_ZONE) continue;

      // Absorbed silently - no death FX, kill credit or bark. It isn't
      // dying, it is being redeployed.
      u.isDead = true;
      particles?.addShockwave?.(u.x, u.y, 16, '#94a3b8', 0.15);
      st.loaded++;
      if (st.loaded === 1) fx.pulse0 = 0.35;
      else if (st.loaded === 2) fx.pulse1 = 0.35;
      else if (st.loaded === 3) fx.pulse2 = 0.35;
    }

    if (st.loaded >= capacity) {
      st.loaded = 0;
      fx.pulse0 = 0; fx.pulse1 = 0; fx.pulse2 = 0;
      bf.launchInvasionCapsule(
        team,
        this.getSlotX(team),
        this.getSlotY(team, entry.slot),
        card,
        particles,
        audio
      );
    }
  }

  // ── Drone Command ───────────────────────────────────────────────────
  //
  // Emits an explosive suicide drone every 10 seconds (600 ticks).
  // The drone targets the nearest enemy ground target in range (50 range = 1200px).
  // If no target is within range when the drone is ready, it waits on the
  // launch pad with spooling rotors (Option A) and launches immediately once
  // a ground target enters range.
  tickDroneCommand(team, dt, particles = null, audio = null) {
    const bf = this.battlefield;
    const stack = this.stack[team];
    let entry = null;
    for (let i = 0; i < stack.length; i++) {
      if (stack[i].moduleId === 'drone_command') { entry = stack[i]; break; }
    }
    if (!entry) return;

    const card = entry.cardData;
    const intervalSec = card?.droneInterval || 10;
    const intervalTicks = Math.round(intervalSec * 60);
    const rangePx = (card?.droneRange || 50) * 24;

    let st = this.state[team].drone_command;
    if (!st) {
      st = { ticks: 0, ready: false };
      this.state[team].drone_command = st;
    }
    let fx = this.fx[team].drone_command;
    if (!fx) {
      fx = { launchFlash: 0, departTimer: 0, departTotal: 0.33 };
      this.fx[team].drone_command = fx;
    }
    if (fx.launchFlash > 0) fx.launchFlash = Math.max(0, fx.launchFlash - dt);
    if (fx.departTimer > 0) fx.departTimer = Math.max(0, fx.departTimer - dt);

    if (st.ticks < intervalTicks) {
      st.ticks++;
      if (st.ticks >= intervalTicks) {
        st.ready = true;
      }
    }

    if (!st.ready) return;

    const slotX = this.getSlotX(team);
    const slotY = this.getSlotY(team, entry.slot);
    const enemyTeam = team === 'western' ? 'eastern' : 'western';

    // Target the closest enemy ground unit within rangePx
    let closestTarget = null;
    let closestDistSq = Infinity;

    for (let i = 0; i < bf.units.length; i++) {
      const u = bf.units[i];
      if (u.team !== enemyTeam || u.isDead || u.isParachuting || u.isCinematic) continue;
      if (u.isAir) continue; // Option 4: Ground units only
      if (u.isCloaked && !bf.isPositionDetected(team, u.x, u.y)) continue;
      if (u.isUntargetable || u.isGoldenStatue) continue;

      const dx = u.x - slotX;
      const dy = u.y - slotY;
      const distSq = dx * dx + dy * dy;
      if (distSq <= rangePx * rangePx && distSq < closestDistSq) {
        closestDistSq = distSq;
        closestTarget = u;
      }
    }

    // If no ground unit in range, check enemy base
    if (!closestTarget && bf.bases[enemyTeam]) {
      const enemyBase = bf.bases[enemyTeam];
      const dx = enemyBase.x - slotX;
      const dy = enemyBase.y - slotY;
      const distSq = dx * dx + dy * dy;
      if (distSq <= rangePx * rangePx) {
        closestDistSq = distSq;
        closestTarget = enemyBase;
      }
    }

    if (closestTarget) {
      st.ticks = 0;
      st.ready = false;
      fx.launchFlash = 0.35;
      fx.departTimer = 0.33;
      fx.departTotal = 0.33;

      bf.launchSuicideDrone(
        team,
        slotX,
        slotY,
        closestTarget,
        card,
        particles,
        audio
      );
    }
  }

  // ── Congress Module ─────────────────────────────────────────────────
  //
  // Cycles through 5 global decrees every 20 seconds (1200 ticks at 60Hz).
  // Decree 0: ADVANCED AMMUNITION (+2 damage for friendly units)
  // Decree 1: TARIFFS (+0.5/s plasma income)
  // Decree 2: PANZERSCHOKOLADE (+1.5 speed for friendly units)
  // Decree 3: QUALITY STEEL (+0.10 deflection rating for friendly units)
  // Decree 4: GOVERNMENT CONTRACTS (drawing cards costs 0 plasma)
  tickCongressModule(team, dt, particles = null, audio = null) {
    const stack = this.stack[team];
    let entry = null;
    for (let i = 0; i < stack.length; i++) {
      if (stack[i].moduleId === 'congress_module') { entry = stack[i]; break; }
    }
    if (!entry) return;

    let st = this.state[team].congress_module;
    let isInitial = false;
    if (!st) {
      st = {
        buffIndex: 0,
        ticksRemaining: CONGRESS_BUFF_INTERVAL_TICKS,
        ticksTotal: CONGRESS_BUFF_INTERVAL_TICKS
      };
      this.state[team].congress_module = st;
      isInitial = true;
    }

    let fx = this.fx[team].congress_module;
    if (!fx) {
      fx = { glowTimer: 0, decreePulse: 0 };
      this.fx[team].congress_module = fx;
    }
    if (fx.glowTimer > 0) fx.glowTimer = Math.max(0, fx.glowTimer - dt);
    if (fx.decreePulse > 0) fx.decreePulse = Math.max(0, fx.decreePulse - dt);
    if (fx.bubble && fx.bubble.timer > 0) {
      fx.bubble.timer = Math.max(0, fx.bubble.timer - dt);
      if (fx.bubble.timer <= 0) fx.bubble = null;
    }

    if (isInitial) {
      const currentBuff = CONGRESS_BUFFS[st.buffIndex];
      const slotX = this.getSlotX(team);
      const slotY = this.getSlotY(team, entry.slot);
      const bannerType = st.buffIndex === 0 ? 'crit' : (st.buffIndex === 1 ? 'heal' : (st.buffIndex === 3 ? 'deflect' : (st.buffIndex === 4 ? 'gold' : 'bonus')));
      particles?.addDamageText?.(slotX, slotY - 18, currentBuff.bannerText, bannerType);
      this.triggerCongressSpeechBubble(team);
      fx.decreePulse = 1.0;
      fx.glowTimer = 1.0;
      if (audio?.playDecreeChime) audio.playDecreeChime();
      else if (audio?.playUpgradeChime) audio.playUpgradeChime();
      else audio?.playSpecialCardAlert?.();
    }

    if (st.ticksRemaining > 0) {
      st.ticksRemaining--;
    }

    if (st.ticksRemaining <= 0) {
      st.buffIndex = (st.buffIndex + 1) % CONGRESS_BUFFS.length;
      st.ticksRemaining = CONGRESS_BUFF_INTERVAL_TICKS;
      st.ticksTotal = CONGRESS_BUFF_INTERVAL_TICKS;

      const currentBuff = CONGRESS_BUFFS[st.buffIndex];
      const slotX = this.getSlotX(team);
      const slotY = this.getSlotY(team, entry.slot);
      const bannerType = st.buffIndex === 0 ? 'crit' : (st.buffIndex === 1 ? 'heal' : (st.buffIndex === 3 ? 'deflect' : (st.buffIndex === 4 ? 'gold' : 'bonus')));
      particles?.addDamageText?.(slotX, slotY - 18, currentBuff.bannerText, bannerType);
      this.triggerCongressSpeechBubble(team);
      fx.decreePulse = 1.0;
      fx.glowTimer = 1.0;
      if (audio?.playDecreeChime) audio.playDecreeChime();
      else if (audio?.playUpgradeChime) audio.playUpgradeChime();
      else audio?.playSpecialCardAlert?.();
    }
  }

  triggerCongressSpeechBubble(team, intro = null) {
    if (!this.has(team, 'congress_module')) return;
    const st = this.state[team]?.congress_module;
    if (!st) return;
    const buff = CONGRESS_BUFFS[st.buffIndex] || CONGRESS_BUFFS[0];
    const introList = CONGRESS_PROCLAMATION_INTROS;
    if (!this._lastCongressIntro) this._lastCongressIntro = {};
    const lastIntro = this._lastCongressIntro[team];
    const available = lastIntro ? introList.filter(item => item !== lastIntro) : introList;
    const introText = intro || available[Math.floor(Math.random() * available.length)];
    this._lastCongressIntro[team] = introText;

    if (!this.fx[team]) this.fx[team] = {};
    if (!this.fx[team].congress_module) this.fx[team].congress_module = { glowTimer: 0, decreePulse: 0 };
    this.fx[team].congress_module.bubble = {
      timer: 3.0,
      total: 3.0,
      intro: introText,
      decree: buff.name.toUpperCase(),
      color: buff.color || '#00ff88'
    };
    if (this.audio?.playDecreeChime) this.audio.playDecreeChime();
  }

  hasCongressBuff(team, buffId) {
    if (!this.has(team, 'congress_module')) return false;
    const st = this.state[team]?.congress_module;
    if (!st) return false;
    const currentBuff = CONGRESS_BUFFS[st.buffIndex];
    if (!currentBuff || currentBuff.id !== buffId) return false;
    return true;
  }

  getCongressState(team) {
    if (!this.has(team, 'congress_module')) return null;
    const st = this.state[team]?.congress_module;
    if (!st) return null;
    const buff = CONGRESS_BUFFS[st.buffIndex] || CONGRESS_BUFFS[0];
    return {
      active: true,
      buffIndex: st.buffIndex,
      buff,
      ticksRemaining: st.ticksRemaining,
      ticksTotal: st.ticksTotal || CONGRESS_BUFF_INTERVAL_TICKS
    };
  }

  _land(team, entry, particles = null, audio = null) {
    this.stack[team].push({
      moduleId: entry.moduleId,
      cardData: entry.cardData,
      hp: entry.hp,
      slot: entry.slot
    });

    // The module's HP becomes part of the base's HP and the combination
    // counts as one building from here on.
    const base = this.battlefield.bases[team];
    if (base && entry.hp > 0) {
      base.maxHp += entry.hp;
      base.hp += entry.hp;
    }

    this.landedSeq++;

    if (!this.fx[team].retractingRigs) this.fx[team].retractingRigs = [];
    this.fx[team].retractingRigs.push({
      slot: entry.slot,
      ticksRemaining: 32,
      ticksTotal: 32
    });

    const px = this.getSlotX(team);
    const py = this.getSlotY(team, entry.slot);
    // World coordinates for particles taking the 0.864 base scale into account (+8%)
    const worldPx = base ? base.x + (px - base.x) * 0.864 : px;
    const worldPy = base ? base.y + (py + MODULE_H / 2 - base.y) * 0.864 : (py + MODULE_H / 2);
    particles?.addShockwave?.(worldPx, worldPy, 32, '#94a3b8', 0.35);
    particles?.addModuleTouchdownDust?.(worldPx, worldPy);
    particles?.addScreenShake?.(2.5, 0.18);
    if (audio?.playModuleDockSlam) audio.playModuleDockSlam();
    else audio?.playExplosion?.('small');
  }

  // ── Netcode (docs/INVARIANTS.md #10.5) ───────────────────────────────

  // Compact form for createGameStateSnapshot(). cardData is intentionally
  // NOT serialized - it is looked up from CARDS_DATA by moduleId on apply,
  // so a snapshot never carries a full card payload across the wire.
  serialize() {
    const out = {};
    for (let t = 0; t < TEAMS.length; t++) {
      const team = TEAMS[t];
      out[team] = {
        stack: this.stack[team].map(m => m.moduleId),
        pending: this.pending[team].map(m => ({
          id: m.moduleId,
          slot: m.slot,
          ticks: m.ticksRemaining
        })),
        state: this.state[team]
      };
    }
    return out;
  }

  // Rebuilds the stack from a host snapshot. Deliberately does NOT touch
  // base.hp/base.maxHp: the snapshot carries both authoritatively (see
  // applyGameStateSnapshot in js/net/stateSync.js) and re-adding module HP
  // here would double-count it on every resync.
  applySerialized(data, resolveCard = null) {
    if (!data) return false;
    for (let t = 0; t < TEAMS.length; t++) {
      const team = TEAMS[t];
      const remote = data[team];
      if (!remote) continue;

      this.stack[team] = (remote.stack || []).map((moduleId, index) => ({
        moduleId,
        cardData: resolveCard ? resolveCard(moduleId) : null,
        hp: 0, // authoritative HP arrives via snapshot.baseMaxHp/baseHp
        slot: index
      }));

      this.pending[team] = (remote.pending || []).map(p => ({
        moduleId: p.id,
        cardData: resolveCard ? resolveCard(p.id) : null,
        hp: 0,
        slot: p.slot,
        ticksRemaining: p.ticks,
        ticksTotal: MODULE_DEPLOY_TICKS
      }));

      if (remote.state) {
        const prevBuffIndex = this.state[team]?.congress_module?.buffIndex;
        this.state[team] = remote.state;
        const newBuffIndex = this.state[team]?.congress_module?.buffIndex;
        if (prevBuffIndex !== undefined && newBuffIndex !== undefined && prevBuffIndex !== newBuffIndex) {
          this.triggerCongressSpeechBubble(team);
        }
      }
    }
    return true;
  }

  // Folded into calcStateHash() so a module that landed on one peer but not
  // the other is caught immediately, instead of surfacing later as an
  // unexplained base-HP difference. Landed modules occupy the low bits,
  // pending ones the high bits.
  hashBits(team) {
    let bits = 0;
    for (const m of this.stack[team]) {
      const idx = MODULE_IDS.indexOf(m.moduleId);
      if (idx >= 0) bits |= (1 << idx);
    }
    for (const m of this.pending[team]) {
      const idx = MODULE_IDS.indexOf(m.moduleId);
      if (idx >= 0) bits |= (1 << (idx + 16));
    }
    return bits;
  }
}

// Module-level constant instead of an inline array literal, so update() and
// serialize() allocate nothing (PERFORMANCE_PARADIGMS #1 - update() runs on
// every simulation tick).
const TEAMS = Object.freeze(['western', 'eastern']);
